// 로더(로봇 팔) 시리얼·구성코드 primitives — docs/plan/loader-history-traceability-2026-07
//
// 라벨 규칙(엑셀 시트 ①): `G2 L 26 07 01` (9자리)
//   G2 = 로봇 종류(GEN2) · L/H = 생산구분(L 외주H5 / H 사내) · 26 연 · 07 월 · 01 순차.
// 초기 1월분은 순차 3자리(G2L2601001, 10자리)라 파서는 두 형식을 관대하게 받는다.
//
// ⚠ 원문 serial 이 유일한 진실원. 아래 디코드 결과(gen/연월/순번)는 화면 표시용
//   best-effort 이며 정렬·매칭·채번 로직이 이에 의존해서는 안 된다.
import { LoaderProductionKind, LOADER_PRODUCTION_KIND_LABELS } from './enums.js';
// [prefixLetter][genDigit][kind L/H][YY][MM][seq(2~3)]
const LOADER_SERIAL_RE = /^([A-Za-z])(\d)([LHlh])(\d{2})(\d{2})(\d+)$/;
/** 로더 시리얼을 디코드(표시용). 형식 불일치면 valid=false, raw 만 채운다. */
export function decodeLoaderSerial(serial) {
    const raw = (serial ?? '').trim();
    const m = LOADER_SERIAL_RE.exec(raw);
    if (!m) {
        return { valid: false, raw, gen: null, productionKind: null, productionYm: null, seq: null };
    }
    const kind = m[3].toUpperCase() === 'L' ? LoaderProductionKind.OUTSOURCED : LoaderProductionKind.IN_HOUSE;
    return {
        valid: true,
        raw,
        gen: Number(m[2]),
        productionKind: kind,
        productionYm: m[4] + m[5],
        seq: Number(m[6]),
    };
}
/** 디코드 결과를 사람이 읽는 한 줄로: "GEN2 · 외주(H5) · 2026-07 · 01번". 실패 시 원문. */
export function formatDecodedLoaderSerial(d) {
    if (!d.valid)
        return d.raw;
    const parts = [];
    if (d.gen != null)
        parts.push(`GEN${d.gen}`);
    if (d.productionKind)
        parts.push(LOADER_PRODUCTION_KIND_LABELS[d.productionKind]);
    if (d.productionYm && d.productionYm.length === 4) {
        parts.push(`20${d.productionYm.slice(0, 2)}-${d.productionYm.slice(2, 4)}`);
    }
    if (d.seq != null)
        parts.push(`${String(d.seq).padStart(2, '0')}번`);
    return parts.join(' · ');
}
/** 편의 — serial 문자열 하나로 바로 표시 문자열. */
export function formatLoaderSerial(serial) {
    return formatDecodedLoaderSerial(decodeLoaderSerial(serial));
}
/**
 * 적용된 개조 코드 집합 → 구성코드(자동, 표시용). 정렬·중복 제거 후 '·' 로 연결.
 * 빈 집합이면 null.
 *   ['E03','E01','E01'] → 'E01·E03'
 */
export function composeLoaderConfigCode(revCodes) {
    const uniq = Array.from(new Set(revCodes.map((c) => c.trim()).filter(Boolean)));
    if (uniq.length === 0)
        return null;
    uniq.sort((a, b) => a.localeCompare(b));
    return uniq.join('·');
}
/**
 * (gen, kind, YYMM, seq) → 시리얼 문자열 조립. 사내 로더 자동 채번에 사용.
 *   composeLoaderSerial(2, IN_HOUSE, '2607', 3) → 'G2H260703'
 */
export function composeLoaderSerial(gen, kind, ym, seq) {
    const k = kind === LoaderProductionKind.OUTSOURCED ? 'L' : 'H';
    return `G${gen}${k}${ym}${String(seq).padStart(2, '0')}`;
}
// ============================================================
// 로더 개조 대장(엑셀) 양식 — docs/plan/loader-rev-excel-upload-2026-09
// ============================================================
//
// 제조팀이 쓰는 `로더_ASSY_이력관리대장_RevNN_YYMMDD.xlsx` 의 구조를 **한 곳에**
// 적어 둔다. 서버 파서와 화면 오류 문구가 같은 상수를 읽어야 "어느 열이 문제인지"
// 가 두 벌로 갈라지지 않는다.
//
// 아래 값은 **실제 대장 파일**(Rev01_260716 / 260731 / 260804)을 읽어 확인한 것이다.
//   시트 4장 중 ①'로더 SN 라벨 규칙'은 설명이라 읽지 않는다.
//   ② 시리얼 마스터 · ③ 개조 적용 매트릭스 는 **머리글이 2행**, 데이터는 3행부터인데
//   3~5행(매트릭스는 3행이 설명, 4~6행)이 양식이 들고 다니는 **예시 행**(S/N 'G2L260~')이다.
//   실제 파일의 표기가 달라지면 파서가 **찾은 열 목록을 그대로 돌려주는 오류**를 내므로,
//   그 메시지를 보고 이 상수만 고치면 된다(코드 다른 곳은 손대지 않는다).
/** 필수 시트 — 이 둘이 없으면 읽지 않는다. `Rev-ECN 매핑` 은 카탈로그가 읽기 전용이라 무시. */
export const LOADER_LEDGER_SHEET_MASTER = '시리얼 마스터';
export const LOADER_LEDGER_SHEET_MATRIX = '개조 적용 매트릭스';
/**
 * 열 이름 비교용 정규화 — 공백·가운뎃점·슬래시·괄호·마침표를 지운다.
 * `입고·채번일` / `입고/채번일` / `입고 채번일` 이 같은 키가 되게(표기 흔들림 흡수).
 */
export function normalizeLedgerHeader(raw) {
    return (raw ?? '').replace(/[\s·/()\-_.]/g, '').toLowerCase();
}
/** 시리얼 마스터 시트의 기대 열. `required` 가 false 면 없어도 통과(값만 못 읽는다). */
export const LOADER_LEDGER_MASTER_COLUMNS = [
    { key: 'serial', label: '로더 S/N', required: true },
    { key: 'kind', label: '구분', required: false },
    { key: 'receivedAt', label: '입고·채번일', required: false },
    { key: 'vendor', label: '외주업체', required: false },
    { key: 'inspection', label: '입고검사 결과', required: false },
    { key: 'notes', label: '비고', required: false },
    // 실제 머리글은 두 줄짜리 '구성코드 / (자동)' — 정규화 후 앞부분 일치로 찾는다.
    { key: 'configCode', label: '구성코드', required: false },
];
/**
 * 매트릭스 시트에서 로더를 가리키는 열(개조 열은 카탈로그에서 동적으로 정한다).
 * 실제 머리글은 두 줄짜리 '로더 S/N / (마스터 자동)' 이라 앞부분 일치로 찾는다.
 */
export const LOADER_LEDGER_MATRIX_SERIAL_LABEL = '로더 S/N';
/**
 * 머리글 이름 맞추기 — 정확히 같거나, **정규화한 앞부분**이 같으면 같은 열로 본다.
 * 대장은 머리글에 설명 줄을 덧붙인다(구성코드+(자동) · 로더 S/N+(마스터 자동)).
 * 순서는 보지 않는다 — 열 하나 옮긴 파일이 통째로 튕기면 현장이 매번 막힌다.
 */
export function matchLedgerHeader(found, expectedLabel) {
    const want = normalizeLedgerHeader(expectedLabel);
    const exact = found.find((f) => f === want);
    if (exact)
        return exact;
    return found.find((f) => f.startsWith(want)) ?? null;
}
/**
 * 대장 양식이 늘 들고 다니는 **예시·설명 행**인지. 사람이 지우지 않고 그 위에 채운다.
 *   · 시리얼 마스터 3~5행 · 매트릭스 4~6행 — S/N 이 'G2L260~'(물결표 자리표시자)
 *   · 매트릭스 3행 — '↓ 항목 설명(직접 수정)'
 * 이 행들을 '제외 목록'에 넣으면 매 업로드마다 가짜 제외 4건이 쌓여 진짜 문제가 묻힌다.
 */
export function isLedgerTemplateRow(rawSerial) {
    const v = (rawSerial ?? '').trim();
    if (!v)
        return true;
    return v.includes('~') || v.startsWith('↓') || v.startsWith('※') || v.startsWith('*');
}
/**
 * 대장에 실릴 수 있는 로더 시리얼 — 오너 규칙 `G2L|G2H + 숫자 6~7자리`.
 *
 * 콘솔 전체 규칙(LOADER_SERIAL_RE)보다 **일부러 좁다**. 콘솔은 GEN1(`G1H48501`)
 * 처럼 대장이 다루지 않는 로더도 받아야 하기 때문이다. 이 규칙에 어긋난 행은
 * 반영하지 않고 '제외 목록'에 사유와 함께 보여 준다.
 */
export const LOADER_LEDGER_SERIAL_RE = /^G2[LH]\d{6,7}$/;
/** 사람이 읽는 규칙 문장 — 제외 사유에 그대로 적어 현장이 왜 빠졌는지 알게 한다. */
export const LOADER_LEDGER_SERIAL_RULE_TEXT = 'G2L 또는 G2H + 숫자 6~7자리';
/** 대조·검사용 정규화(앞뒤 공백 제거 + 대문자). 저장은 원문을 유지한다. */
export function normalizeLedgerSerial(raw) {
    return (raw ?? '').trim().toUpperCase();
}
export function isLedgerSerial(raw) {
    return LOADER_LEDGER_SERIAL_RE.test(normalizeLedgerSerial(raw));
}
/**
 * 대장 파일명에서 기준일을 읽는다 — `로더_ASSY_이력관리대장_Rev01_260731.xlsx` → 2026-07-31.
 * `RevNN_YYMMDD` 를 찾고, 없으면 파일명 어디든 있는 6자리 날짜를 쓴다. 둘 다 없으면 null.
 * 20YY 로 해석한다(대장은 2026년부터 쓰인다).
 */
export function parseLedgerBaseDateFromFileName(fileName) {
    const name = fileName ?? '';
    const m = /rev\s*\d*[_\-\s]*(\d{2})(\d{2})(\d{2})/i.exec(name) ?? /(\d{2})(\d{2})(\d{2})/.exec(name);
    if (!m)
        return null;
    const [, yy, mm, dd] = m;
    const month = Number(mm);
    const day = Number(dd);
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return null;
    return `20${yy}-${mm}-${dd}`;
}
/** 구성코드 문자열을 코드로 — 대장은 `E01+E03`, 콘솔은 `E01·E03` 로 쓴다. 둘 다 받는다. */
export function splitLedgerConfigCode(raw) {
    return (raw ?? '')
        .split(/[+·,/]/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
}
/** 매트릭스 머리글의 개조 코드 표기 정규화 — `Rev.E01` / `Rev E01` / `E01` → `E01`. */
export function normalizeLedgerRevCode(raw) {
    return (raw ?? '')
        .trim()
        .replace(/^rev\.?\s*/i, '')
        .toUpperCase();
}
/** 매트릭스 칸이 '적용'인지 — `O`/`○`/`ㅇ`/`v`/`Y` 계열을 관대하게 받는다. 빈칸·`-` 는 미적용. */
export function isLedgerAppliedMark(raw) {
    const v = (raw ?? '').trim();
    if (!v || v === '-' || v === 'x' || v === 'X')
        return false;
    return /^[oO0○◯●ㅇvVyY√✓]$/.test(v);
}
//# sourceMappingURL=loader.js.map